import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Milk, Loader2, Plus, Pencil, Trash2, Info, ChevronDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const LEER = { code: "", name: "", beschreibung: "" };

// Gängige Konventionen für Milchsorten-Codes im ÖNORM-S-1052-Datenträger.
// Diese Codes sind NICHT gesetzlich vorgeschrieben – jede Molkerei kann eigene
// Codes vergeben. Die Auswahl folgt der Praxis von AMA / QSMilch / VLOG
// und dient als Ausgangspunkt. Ab Code 07 stehen freie Slots für Sondersorten.
const GAENGIGE_CODES = [
  { code: "01", name: "Konventionell", beschreibung: "Standard-Rohmilch ohne Zertifizierung" },
  { code: "02", name: "Heumilch g.t.S.", beschreibung: "Heumilch (garantiert traditionelle Spezialität EU 2016/304), silage­frei" },
  { code: "03", name: "Bio", beschreibung: "Biomilch nach EU-Öko-Verordnung 2018/848" },
  { code: "04", name: "Bio-Heumilch", beschreibung: "Kombiniert Bio + Heumilch-Kriterien" },
  { code: "05", name: "Weidemilch", beschreibung: "Kühe mit mind. 120 Weidetagen à ≥6 Stunden pro Jahr" },
  { code: "06", name: "Gentechnikfrei (VLOG)", beschreibung: "Ohne Gentechnik erzeugt nach VLOG-Standard" },
  { code: "07", name: "ARGE Heumilch Premium", beschreibung: "Heumilch mit Prämiensegment (regional)" },
  { code: "09", name: "Sondersorte", beschreibung: "Individuell definierte Sorte (frei belegbar)" },
];

export default function AdminMilchsortenPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/milchsorten").then((res) => setRows(res.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const speichern = async () => {
    setSaving(true);
    try {
      if (form.id) await api.put(`/milchsorten/${form.id}`, form);
      else await api.post("/milchsorten", form);
      toast.success("Milchsorte gespeichert.");
      setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const loeschen = async () => {
    try {
      await api.delete(`/milchsorten/${del.id}`);
      toast.success("Milchsorte gelöscht.");
      setDel(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Milchsorten</h1>
          <p className="text-sm text-muted-foreground">
            Klassifizierung der angelieferten Milch. Preise, Zuschläge und Abzüge (inkl. GVO-frei-Bonus)
            werden ausschließlich im Preismodell je Sorte gepflegt.
          </p>
        </div>
        <Button data-testid="milktype-create-button" onClick={() => setForm({ ...LEER })}>
          <Plus className="h-4 w-4 mr-2" /> Neue Sorte
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : rows.length ? (
            <Table data-testid="milktypes-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Beschreibung</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-num">{r.code}</TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[420px] truncate">{r.beschreibung}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button data-testid={`milktype-edit-button-${r.code}`} variant="ghost" size="icon" onClick={() => setForm({ ...r })}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button data-testid={`milktype-delete-button-${r.code}`} variant="ghost" size="icon" onClick={() => setDel(r)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState icon={Milk} title="Noch keine Milchsorten angelegt." />
          )}
        </CardContent>
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="milktype-form-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>{form?.id ? "Milchsorte bearbeiten" : "Neue Milchsorte"}</DialogTitle></DialogHeader>
          {form && (() => {
            // Codes markieren, die bereits von anderen Sorten belegt sind
            const belegt = new Set(
              rows.filter((r) => r.id !== form.id).map((r) => (r.code || "").trim()),
            );
            const vorschlag = GAENGIGE_CODES.find((c) => c.code === (form.code || "").trim());
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Code *</Label>
                    <div className="flex gap-1">
                      <Input
                        data-testid="milktype-form-code"
                        value={form.code}
                        onChange={(e) => setForm({ ...form, code: e.target.value })}
                        placeholder="05"
                        className="font-num"
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            type="button"
                            data-testid="milktype-code-suggestions"
                            title="Gängige Codes vorschlagen"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[340px]">
                          <DropdownMenuLabel className="text-xs">
                            Gängige Konventionen (AMA / VLOG / ÖNORM S 1052)
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {GAENGIGE_CODES.map((c) => {
                            const istBelegt = belegt.has(c.code);
                            return (
                              <DropdownMenuItem
                                key={c.code}
                                disabled={istBelegt}
                                onSelect={() => setForm((f) => ({
                                  ...f,
                                  code: c.code,
                                  // Name/Beschreibung nur setzen, wenn noch leer
                                  name: f.name || c.name,
                                  beschreibung: f.beschreibung || c.beschreibung,
                                }))}
                                data-testid={`milktype-code-suggestion-${c.code}`}
                                className="flex-col items-start gap-0.5 py-2"
                              >
                                <div className="flex items-center gap-2 w-full">
                                  <span className="font-num font-semibold text-sm min-w-[28px]">{c.code}</span>
                                  <span className="font-medium text-sm">{c.name}</span>
                                  {istBelegt && (
                                    <CheckCircle2 className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
                                  )}
                                </div>
                                <p className="text-[11px] text-muted-foreground pl-[36px]">
                                  {c.beschreibung}
                                  {istBelegt && " · bereits vergeben"}
                                </p>
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {vorschlag && form.code !== "01" && (
                      <p className="text-[11px] text-muted-foreground" data-testid="milktype-code-hint">
                        <Info className="h-3 w-3 inline mr-1" />
                        Übliche Bezeichnung: <span className="font-medium">{vorschlag.name}</span>
                      </p>
                    )}
                    {form.code && belegt.has((form.code || "").trim()) && (
                      <p className="text-[11px] text-destructive font-medium" data-testid="milktype-code-conflict">
                        Code bereits vergeben.
                      </p>
                    )}
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label>Name *</Label>
                    <Input data-testid="milktype-form-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Weidemilch" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Beschreibung</Label>
                  <Textarea value={form.beschreibung}
                    onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} rows={2} />
                </div>
                <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground space-y-2" data-testid="milktype-form-info">
                  <p className="flex items-start gap-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                    <span>
                      <span className="font-medium text-foreground">Codes sind pro Molkerei frei wählbar.</span>
                      {" "}Es gibt keinen gesetzlich verpflichtenden Milchsorten-Code – die Codes werden aber
                      im ÖNORM-S-1052-Datenträger (Erfassungssystem im Sammelwagen) verwendet und sollten
                      daher innerhalb der Molkerei eindeutig und stabil bleiben. Die vorgeschlagenen Codes
                      orientieren sich an gängiger AMA-/VLOG-Praxis.
                    </span>
                  </p>
                  <p className="pl-5">
                    Grundpreis, Klassen-Zuschläge, GVO-frei-Bonus und Mengenstaffel werden ausschließlich
                    in der Preismodell-Revision zu dieser Sorte gepflegt.
                  </p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Abbrechen</Button>
            <Button data-testid="milktype-form-save-button" onClick={speichern}
              disabled={saving || !form?.code || !form?.name}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Milchsorte löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {del?.name} ({del?.code}) wird gelöscht. Vorhandene Preismodell-Revisionen und
              Abrechnungen bleiben unberührt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={loeschen}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
